import { Box, Grid, IconButton, TableCell, tableCellClasses, TableRow, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import ButtonContained from '../../../Common/ButtonContained'
import CustomDialog from '../../../Common/CustomDialog';
import CommonTextField from '../../../Common/CommonTextField';
import TextLabel from '../../../Common/CommonTextLabel';
import CommonFileUpload from '../../../Common/CommonFileUpload';
import { useTheme } from '@emotion/react';
import { showLoader } from '../../../../Redux/Slices/loaderSlice';
import { useDispatch } from 'react-redux';
import { addBanner, deleteBanner, getBanner, updateBanner, uploadImage } from '../../../../Redux/Actions/action';
import ErrorHandler from '../../../../Utils/handler';
import { toast } from 'react-toastify';
import CustomPagination from '../../../Common/CommonPagination';
import CommonTable from '../../../Common/CommonTable';
import { BucketURL } from '../../../../ApiSetup/Api';
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DataNotFound from '../../../Common/DataNotFound';
import Assets from '../../../Common/Assets';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { styled } from "@mui/material/styles";

const headerList = ["No", "Banner Name", "Image/Video", "Edit", "Delete"]
const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        color: theme?.palette?.bgLightWhite?.main,
        fontSize: 14,
        // fontWeight: "bold",
        textAlign: "center", // Horizontally align content,
        lineHeight: '1.2rem'
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        padding: '0px 10px !important',
        textAlign: "center", // Horizontally align content
        borderBottom: "2px solid #d9d6f3",
    },
}));

function Banner() {
    // ---------- Hook -----------
    const theme = useTheme()
    const dispatch = useDispatch()

    // --------------- State -------------
    const [open, setOpen] = useState(false);
    const [data, setData] = useState({});
    const [bannerList, setBannerList] = useState({});
    const [filePreView, setFilePreView] = useState({ isOpen: false, link: "", type: "" });
    const [error, setError] = useState({});
    const [isEdit, setIsEdit] = useState(false);
    const [page, setPage] = React.useState(1);
    const [rowsPerPage, setRowsPerPage] = React.useState(5);

    // --------------- handleChange ---------------
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setData({ ...data, [name]: value.trimStart() });
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(1);
    };

    const handleClear = () => {
        setOpen(false);
        setIsEdit(false);
        setData({});
        setError({})
    };

    const validation = () => {
        let isValid = true;
        let error = {}
        if (!data.bannerName) {
            error.bannerName = "Banner Name is required";
            isValid = false;
        }
        if (!data.bannerUrl) {
            error.bannerUrl = "Banner URL is required";
            isValid = false;
        }
        setError(error);
        return isValid;
    }

    // --------------- Api ---------------
    const handleImageUpload = async (e) => {
        const { files, name } = e?.target;

        const validImageTypes = ['image/jpeg', 'image/png', 'image/jpg'];
        const validVideoTypes = ['video/mp4', 'video/webm', 'video/ogg'];
        const validTypes = [...validImageTypes, ...validVideoTypes];

        const fileData = files[0];
        if (!validTypes.includes(fileData.type)) {
            setError((error) => ({
                ...error,
                bannerImageSize: 'Invalid file type. Only JPEG, PNG, JPG, MP4, WebM, and OGG are allowed.',
            }));
            return;
        }

        if (fileData.size > 10485760) {
            setError((error) => ({
                ...error,
                bannerImageSize: 'File size exceeds the 10 MB limit. Please upload a smaller file.',
            }));
            return;
        }

        const formData = new FormData();
        formData.append("image", files[0]);

        dispatch(showLoader(true));
        try {
            const result = await dispatch(uploadImage(formData));
            const error = ErrorHandler(result);

            if (error) {
                const uploadedFile = result?.payload?.data?.[0]?.image;

                setData((prevState) => ({
                    ...prevState,
                    [name]: uploadedFile,
                    isImage: validImageTypes.includes(fileData.type)
                }));

                setError((error) => ({
                    ...error,
                    bannerImageSize: '',
                    bannerImage: '',
                }));
            }
        } catch (err) {
            console.error('File upload failed:', err);
        } finally {
            dispatch(showLoader(false));
        }
    };

    const _createUpdateBanner = async () => {
        if (validation()) {
            dispatch(showLoader(true))
            try {
                let body = {
                    bannerName: data?.bannerName,
                    bannerUrl: data?.bannerUrl,
                    isImage: data?.isImage,
                }
                if (isEdit && data?._id) {
                    body._id = data?._id;
                }
                const fn = isEdit ? updateBanner : addBanner
                const result = await dispatch(fn(body))
                const error = ErrorHandler(result)
                if (error) {
                    toast.success(result?.payload?.message);
                    _getBanner()
                    handleClear()
                }
            } catch (error) {
                toast.error(error?.message || "An unknown error occurred");
            } finally {
                dispatch(showLoader(false))
            }
        }
    }

    const _getBanner = async () => {
        dispatch(showLoader(true))
        let body = {
            page: page,
            limit: rowsPerPage
        }
        try {
            const result = await dispatch(getBanner(body));
            const error = ErrorHandler(result);
            if (error) {
                // toast.success(result?.payload?.message);
                setBannerList(result?.payload?.data);
            }
        } catch (error) {
            toast.error(error);
        } finally {
            dispatch(showLoader(false))
        }
    }

    const _deleteBanner = async (id) => {
        dispatch(showLoader(true))
        try {
            const result = await dispatch(deleteBanner(id));
            console.log(result, 'deletefaq')
            const error = ErrorHandler(result);
            if (error) {
                toast.success(result?.payload?.message);
                _getBanner()
            }
        } catch (error) {
            toast.error(error);
        } finally {
            dispatch(showLoader(false))
        }
    }

    // ---------- useEffect ------------ 
    useEffect(() => {
        _getBanner()
    }, [page, rowsPerPage])

    return (
        <>
            <Grid container spacing={1} xs={12}>
                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '20px', fontWeight: 500 }}>Banner</Typography>
                        <ButtonContained text={'Add Banner'} onClick={() => setOpen(true)} />
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <CommonTable
                        headerList={headerList}
                        minWidth={400}
                    // sx={{ mt: 2 }}
                    >
                        {bannerList?.response?.length > 0 ? bannerList?.response?.map((banner, index) => (
                            <TableRow key={banner.id}>
                                <StyledTableCell align="center">{(index % rowsPerPage) + 1 + (page - 1) * rowsPerPage}</StyledTableCell>
                                <StyledTableCell align="center">{banner?.bannerName}</StyledTableCell>
                                <StyledTableCell align="center">
                                    <IconButton sx={{ padding: 1 }} onClick={() => {
                                        const url = BucketURL + banner?.bannerUrl;
                                        const type = banner?.bannerUrl?.match(/\.(jpeg|jpg|png)$/) ? 'image' : 'video'; // Check if it's an image or video
                                        setFilePreView({ isOpen: true, link: url, type: type });
                                    }}>
                                        <VisibilityIcon fontSize='medium' color='primary' />
                                    </IconButton>
                                </StyledTableCell>
                                <StyledTableCell align="center">
                                    <IconButton sx={{ padding: 0 }} onClick={() => { setOpen(true); setIsEdit(true); setData(banner) }} >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </StyledTableCell>
                                <StyledTableCell align="center">
                                    <IconButton sx={{ padding: 0 }} onClick={() => { _deleteBanner(banner?._id) }} >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </StyledTableCell>
                            </TableRow>
                        )) :
                            (<TableRow>
                                <StyledTableCell colSpan={8}>
                                    <DataNotFound title={'No Records Found'} icon={<ErrorOutlineIcon color="primary" style={{ fontSize: "3rem" }} />} elevation={0} />
                                </StyledTableCell>
                            </TableRow>)
                        }
                    </CommonTable>
                    {bannerList?.count > 0 &&
                        <Grid item xs={12}>
                            <CustomPagination
                                rowsPerPageOptions={[5, 10, 25, 50]}
                                count={Math.ceil(bannerList?.count / rowsPerPage)}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage} />

                        </Grid>}
                </Grid>
                {
                    open && <CustomDialog
                        isOpen={open} handleClose={handleClear} title={isEdit ? 'Update Banner' : 'Add Banner'} padding={'10px'}
                        width={400}
                        action={
                            <>
                                <ButtonContained
                                    text={isEdit ? 'Update Banner' : 'Add Banner'}
                                    onClick={_createUpdateBanner}
                                    minWidth={'100%'}
                                    marginTop={'10px'}
                                />
                            </>
                        }
                    >
                        <Grid container spacing={2} xs={12} ml={-1}>
                            <Grid item xs={12}>
                                <CommonFileUpload
                                    text={"Banner Image"}
                                    padding={{ xs: "5px 14px", sm: "30px 14px", lg: "35px 7px" }}
                                    accept="image/jpeg, image/png, image/jpg, video/mp4, video/webm, video/ogg"
                                    labelTitle="image/jpeg, image/png, image/jpg, video/mp4, video/webm, video/ogg Up to 10MB"
                                    name={"bannerUrl"} // This is the key that will be logged in handleUpload
                                    onChange={(e) => handleImageUpload(e)}
                                    valid
                                />
                                {data?.bannerUrl && <Assets src={BucketURL + data?.bannerUrl} height={'50px'} width={'50px'} alt="Banner Image" />}
                                <TextLabel title={!data?.bannerUrl ? error?.bannerUrl : ""} color="error" />
                                <TextLabel title={data?.bannerImageSize ? "" : error?.bannerImageSize} color="error" />
                            </Grid>
                            <Grid item xs={12}>
                                <CommonTextField
                                    value={data?.bannerName}
                                    width={"100%"}
                                    text="Banner"
                                    type="text"
                                    onChange={(e) => handleInputChange(e)}
                                    placeholder="Enter a Banner Name"
                                    name="bannerName"
                                    valid
                                />
                                <TextLabel title={!data?.bannerName ? error?.bannerName : ""} color="error" />
                            </Grid>
                        </Grid>
                    </CustomDialog>}

                {filePreView?.isOpen &&
                    <CustomDialog
                        isOpen={filePreView?.isOpen} handleClose={() => setFilePreView({ isOpen: false, link: "", type: "" })} title={"View Banner"} padding={'5px'}
                        width={400}
                        action={
                            filePreView?.type === "image" ? (<img src={filePreView?.link} alt="Preview" style={{ width: '100%' }} />
                            ) : filePreView?.type === "video" ?
                                (<video controls style={{ width: '100%' }}>
                                    <source src={filePreView?.link} type="video/mp4" />
                                    Your browser does not support the video tag.
                                </video>)
                                : (<div>No media available</div>)
                        }
                        maxWidth={'md'}
                    >
                    </CustomDialog>}

            </Grid>
        </>
    )
}

export default Banner
